/**
 * Explosion — extracted default explosion logic for projectile impacts.
 *
 * Called by the firing loop on normal impact, and by custom weapon onImpact
 * callbacks that want "standard explosion plus spawns."
 */

import type { Projectile, GameState } from '../types';
import type { GameEffect } from './effects';
import { executeCraterPipeline, fillNoisyCircle, isPointInTerrain, getGroundY } from '../terrain/grid';
import { applyExplosionDamage, applyBlastKnockback } from './damage';
import { unsettleTanksNearCrater } from '../physics/tanks';
import { spawnSmoothingRegion } from '../terrain/sand-physics';
import { HITLAG, PHYSICS, STRUCTURAL, GLITCH, GLITCH_SHOCKWAVE_RING, GLITCH_GRAVITY_WELL, GLITCH_CHAIN_SHOCK } from '../constants';
import { getTankShot, lookupTank } from '../tanks';
import { cancelGrapple } from './grapple';
import { modeIsClassic } from './mode';

/** Convert milliseconds to fixed-update frame count (60fps). */
const msToFrames = (ms: number) => Math.round(ms * 60 / 1000);

/** Track whether a sub-projectile (gen 1+) has already triggered shake this volley. */
let _subShakeTriggered = false;

/** Reset sub-explosion tracking — call when firing phase exits. */
export function resetSubExplosionTracking(): void { _subShakeTriggered = false; }

export interface ExplosionResult {
  knockedPlayers: number[];
  hitPlayers: number[];
  damages: number[];
}

/**
 * Execute a default explosion at (x, y): crater + damage + knockback + effects.
 * Returns the indices of knocked players for DI processing.
 */
export function defaultExplosion(
  proj: Projectile,
  x: number, y: number,
  state: GameState,
  fx: GameEffect[],
): ExplosionResult {
  const weapon = proj.weapon;
  const blastRadius = weapon.blastRadius;
  const color = weapon.color;
  const firedBy = proj.owner;

  // Terrain-creating weapons — fill instead of destroy
  if (weapon.fillsTerrain) {
    const fillResult = fillNoisyCircle(x, y, blastRadius, weapon.craterShape);
    if (fillResult) {
      fx.push({ type: 'terrain_update', bounds: fillResult });
      spawnSmoothingRegion(state, fillResult);
      unsettleTanksNearCrater(state.players, x, blastRadius);
      // Push buried tanks above the new terrain surface
      for (const p of state.players) {
        if (!p.alive) continue;
        if (isPointInTerrain(p.x, p.y)) {
          const surfaceY = getGroundY(p.x);
          p.y = surfaceY - PHYSICS.ECB_RADIUS;
          p.vy = Math.min(p.vy, 0); // don't push them downward
        }
      }
    }
    // Minimal damage + knockback (the value is terrain creation)
    const { hitPlayers, damages } = applyExplosionDamage(state, x, y, blastRadius, weapon.damage, firedBy);
    const knockedPlayers = applyBlastKnockback(
      state, x, y, blastRadius * weapon.knockbackRadius,
      blastRadius * weapon.knockbackMult, firedBy, weapon,
    );
    // Visual
    fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [x, y, color, blastRadius, weapon.particleScale * 0.5] });
    fx.push({ type: 'camera_shake', intensity: blastRadius / weapon.shakeIntensity, decay: 0.85 });
    fx.push({ type: 'screen_flash', color, alpha: 0.1 });
    fx.push({ type: 'bg_explosion', x, y, radius: blastRadius });
    return { knockedPlayers, hitPlayers, damages };
  }

  // Standard explosion — crater + damage + knockback
  if (!weapon.noCrater) {
    const craterResult = executeCraterPipeline(
      x, y, blastRadius, weapon.craterShape,
      weapon.canDestroyWireframe ?? false,
      weapon.wireframeDamageScale ?? 1.0,
    );
    if (craterResult) {
      fx.push({ type: 'terrain_update', bounds: craterResult.finalBounds });
      fx.push({ type: 'terrain_add_crater', x, y, radius: blastRadius });
      if (proj.generation === 0 || Math.random() < HITLAG.SUB_TERRAIN_DEBRIS_CHANCE) {
        fx.push({ type: 'particles', method: 'spawnTerrainDebris', args: [x, y, blastRadius, weapon.particleScale] });
      }
      spawnSmoothingRegion(state, craterResult.finalBounds);
      unsettleTanksNearCrater(state.players, x, blastRadius);

      // Structural collapse — wireframe fragment debris + sand cascade
      if (craterResult.collapse) {
        const c = craterResult.collapse;
        for (const centroid of c.centroids) {
          fx.push({ type: 'particles', method: 'spawnStructuralDebris', args: [centroid.x, centroid.y, c.cells] });
        }
        const shakeIntensity = Math.min(
          STRUCTURAL.SHAKE_BASE + c.cells * STRUCTURAL.SHAKE_PER_CELL,
          STRUCTURAL.SHAKE_MAX,
        );
        fx.push({ type: 'screen_shake', intensity: shakeIntensity, decay: STRUCTURAL.SHAKE_DECAY });
        spawnSmoothingRegion(state, c.bounds);
        for (const centroid of c.centroids) {
          unsettleTanksNearCrater(state.players, centroid.x, 60);
        }
      }
    }
  }

  // Visual
  const isSub = proj.generation > 0;
  fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [x, y, color, blastRadius, weapon.particleScale] });
  if (isSub) {
    if (!_subShakeTriggered) {
      fx.push({ type: 'camera_shake', intensity: blastRadius / weapon.shakeIntensity, decay: 0.85 });
    }
    fx.push({ type: 'screen_flash', color, alpha: HITLAG.SUB_FLASH_ALPHA });
  } else {
    fx.push({ type: 'camera_shake', intensity: blastRadius / weapon.shakeIntensity, decay: 0.85 });
    fx.push({ type: 'screen_multi_flash', color });
  }
  fx.push({ type: 'bg_explosion', x, y, radius: blastRadius });

  // Damage + knockback
  const { hitPlayers, damages } = applyExplosionDamage(state, x, y, blastRadius, weapon.damage, firedBy);
  const knockedPlayers = applyBlastKnockback(
    state, x, y, blastRadius * weapon.knockbackRadius,
    blastRadius * weapon.knockbackMult, firedBy, weapon,
  );

  // Classic mode: check for HP kills
  if (modeIsClassic(state.modeState.mode)) {
    for (const pi of hitPlayers) {
      if (state.players[pi].hp <= 0 && !state.players[pi].pendingDeath) {
        state.players[pi].pendingDeath = true;
        fx.push({ type: 'death_triggered', playerIndex: pi, deathType: 'hp', x: state.players[pi].x, y: state.players[pi].y });
      }
    }
  }

  // Weapon Lab hit data capture (training mode)
  if (state.modeState.mode === 'training' && hitPlayers.length > 0) {
    const hitIdx = hitPlayers[0];
    const hitP = state.players[hitIdx];
    const kbVx = hitP.diPendingVx || hitP.vx;
    const kbVy = hitP.diPendingVy || hitP.vy;
    const kbMag = Math.sqrt(kbVx * kbVx + kbVy * kbVy);
    const kbAngle = Math.atan2(-kbVy, kbVx) * (180 / Math.PI);
    const attacker = state.players[firedBy];
    state.modeState.labLastHit = {
      weaponName: weapon.name,
      tier: attacker?.selectedShotTier ?? 1,
      damage: damages[0],
      totalDamage: hitP.damagePercent,
      knockbackMag: kbMag,
      knockbackAngle: kbAngle,
      blastRadius,
    };
  }

  // Hitstop (with shot tier hitlag bonus)
  let hitstopMs: number;
  if (isSub) {
    // Sub-projectiles: skip terrain hitstop entirely, short hitstop on player hit
    hitstopMs = hitPlayers.length > 0 ? HITLAG.SUB_HITLAG_MS : 0;
  } else {
    hitstopMs = hitPlayers.length > 0 ? HITLAG.HITLAG_MS : HITLAG.TERRAIN_HITLAG_MS;
    if (hitPlayers.length > 0) {
      const attacker = state.players[firedBy];
      if (attacker) {
        const shotDef = getTankShot(attacker.tankId, attacker.selectedShotTier, attacker.terraformerMode);
        hitstopMs += shotDef.hitlagBonus;
      }
    }
  }
  if (hitstopMs > 0) {
    state.freezeUntil = Math.max(state.freezeUntil, state.frameCount + msToFrames(hitstopMs));
  }

  // Hit effects
  if (hitPlayers.length > 0) {
    if (isSub) {
      // Sub-projectiles: first gets full hit cross + shake + text
      if (!_subShakeTriggered) {
        fx.push({ type: 'particles', method: 'spawnHitCross', args: [x, y, blastRadius] });
        fx.push({ type: 'screen_shake', intensity: HITLAG.HIT_SHAKE, decay: HITLAG.HIT_SHAKE_DECAY });
      } else {
        // Subsequent sub-hits: dimmed/smaller hit cross so they're not invisible
        fx.push({ type: 'particles', method: 'spawnHitCross', args: [x, y, blastRadius * 0.5] });
      }
      for (let j = 0; j < hitPlayers.length; j++) {
        const pi = hitPlayers[j];
        const p = state.players[pi];
        // Skip knockback indicator for subs — DI shows final accumulated knockback
        fx.push({ type: 'tank_flash', playerIndex: pi, bright: 0xff4444, dark: 0x220000, duration: 0.3 });
        if (!_subShakeTriggered) {
          const dmg = damages[j];
          const { text: hitLabel, color: hitColor, fontSize: hitSize } = getHitTier(dmg);
          const offsetX = (Math.random() - 0.5) * 30;
          fx.push({ type: 'announce_world', text: hitLabel, x: p.x + offsetX, y: p.y - 50, color: hitColor, fontSize: hitSize });
        }
      }
    } else {
      fx.push({ type: 'particles', method: 'spawnHitCross', args: [x, y, blastRadius] });
      fx.push({ type: 'screen_shake', intensity: HITLAG.HIT_SHAKE, decay: HITLAG.HIT_SHAKE_DECAY });
      for (let j = 0; j < hitPlayers.length; j++) {
        const pi = hitPlayers[j];
        const p = state.players[pi];
        fx.push({ type: 'particles', method: 'spawnKnockbackIndicator', args: [p.x, p.y, p.diPendingVx, p.diPendingVy, p.color] });
        fx.push({ type: 'tank_flash', playerIndex: pi, bright: 0xff4444, dark: 0x220000, duration: 0.3 });
        // Tiered hit impact text
        const dmg = damages[j];
        const { text: hitLabel, color: hitColor, fontSize: hitSize } = getHitTier(dmg);
        const offsetX = (Math.random() - 0.5) * 30;
        fx.push({ type: 'announce_world', text: hitLabel, x: p.x + offsetX, y: p.y - 50, color: hitColor, fontSize: hitSize });
      }
    }
  } else {
    // Terrain-only hit — increased feedback so it reads as "near miss" not "nothing"
    if (!isSub || !_subShakeTriggered) {
      fx.push({ type: 'screen_shake', intensity: HITLAG.TERRAIN_SHAKE, decay: HITLAG.TERRAIN_SHAKE_DECAY });
      fx.push({ type: 'screen_flash', color, alpha: 0.15 });
    }
  }

  // Mark sub-shake as triggered after first sub-explosion
  if (isSub) _subShakeTriggered = true;

  // ── Glitch post-explosion effects ──

  // SHOCKWAVE_RING: secondary outward push from every explosion
  if (state.activeGlitch === GLITCH_SHOCKWAVE_RING) {
    let shockHit = false;
    for (const p of state.players) {
      if (!p.alive || p.respawnInvulnFrames > 0) continue;
      const dx = p.x - x;
      const dy = p.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 0 && dist < GLITCH.SHOCKWAVE_RING_RADIUS) {
        const falloff = 1 - dist / GLITCH.SHOCKWAVE_RING_RADIUS;
        const force = GLITCH.SHOCKWAVE_RING_FORCE * falloff;
        p.vx += (dx / dist) * force;
        p.vy += (dy / dist) * force;
        p.settled = false;
        shockHit = true;
      }
    }
    if (shockHit) cancelGrapple(state);
    fx.push({ type: 'screen_shake', intensity: 3, decay: 0.9 });
  }

  // GRAVITY_WELL: pull all tanks toward the explosion
  if (state.activeGlitch === GLITCH_GRAVITY_WELL) {
    let wellHit = false;
    for (const p of state.players) {
      if (!p.alive || p.respawnInvulnFrames > 0) continue;
      const dx = p.x - x;
      const dy = p.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 0 && dist < GLITCH.SHOCKWAVE_RING_RADIUS) {
        const falloff = 1 - dist / GLITCH.SHOCKWAVE_RING_RADIUS;
        const force = GLITCH.GRAVITY_WELL_FORCE * falloff;
        p.vx -= (dx / dist) * force;
        p.vy -= (dy / dist) * force;
        p.settled = false;
        wellHit = true;
      }
    }
    if (wellHit) cancelGrapple(state);
  }

  // CHAIN_SHOCK: damage chains from hit tanks to nearby bystanders
  if (state.activeGlitch === GLITCH_CHAIN_SHOCK && hitPlayers.length > 0) {
    const hitSet = new Set(hitPlayers);
    for (const hi of hitPlayers) {
      const hp = state.players[hi];
      for (let j = 0; j < state.players.length; j++) {
        if (hitSet.has(j)) continue;
        const other = state.players[j];
        if (!other.alive || other.respawnInvulnFrames > 0) continue;
        const otherEcb = lookupTank(other.tankId).ecbRadius ?? PHYSICS.ECB_RADIUS;
        const dx = other.x - hp.x;
        const dy = other.y - hp.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < GLITCH.CHAIN_SHOCK_RADIUS + otherEcb) {
          const chainDamage = weapon.damage * GLITCH.CHAIN_SHOCK_DAMAGE_MULT;
          other.damagePercent += chainDamage;
          hitSet.add(j); // prevent double-chaining
          fx.push({ type: 'particles', method: 'spawnHitCross', args: [other.x, other.y, 15] });
          fx.push({ type: 'tank_flash', playerIndex: j, bright: 0x4444ff, dark: 0x000022, duration: 0.2 });
        }
      }
    }
  }

  return { knockedPlayers, hitPlayers, damages };
}

/** Get tiered hit text based on damage dealt. Fighting game bold. */
function getHitTier(damage: number): { text: string; color: number; fontSize: number } {
  if (damage >= 60) return { text: 'OBLITERATED!', color: 0xff0044, fontSize: 36 };
  if (damage >= 35) return { text: 'DEVASTATING!', color: 0xff4400, fontSize: 30 };
  if (damage >= 15) return { text: 'SMASH!', color: 0xffaa00, fontSize: 24 };
  return { text: 'HIT!', color: 0xffffff, fontSize: 18 };
}
